/**
 * Locale primitives.
 *
 * Kept in their own module (rather than in `index.ts`) so that `format.ts` and the
 * test suite can use them without pulling in `i18next` or the JSON catalogues.
 * `index.ts` re-exports everything here — import from `@horizon/i18n`.
 */

/** Arabic is first, and it is the default. French is equal, but secondary. */
export const SUPPORTED_LOCALES = ['ar', 'fr'] as const

export type Locale = (typeof SUPPORTED_LOCALES)[number]

export const DEFAULT_LOCALE: Locale = 'ar'

export type Direction = 'rtl' | 'ltr'

/**
 * Per-locale metadata.
 *
 * `lineHeight` is the typography line-height variant key from
 * `docs/04-design-system.md` §2.3 — `@horizon/tokens` stores two line heights per type
 * token (`lineHeight.ar` / `lineHeight.fr`) because Arabic needs the taller one for its
 * ascenders, descenders, and diacritics. The `Text` component reads this key.
 */
export interface LocaleMeta {
  /** BCP-47 tag, for `Intl` and for the `lang` attribute. */
  readonly code: Locale
  /** The language's own name, shown in the language picker in both languages. */
  readonly nativeName: string
  /** English name, for logs and admin tooling only — never rendered to a student. */
  readonly englishName: string
  readonly direction: Direction
  /** Key into `tokens.type[*].lineHeight`. */
  readonly lineHeight: 'ar' | 'fr'
  /** Font family role from §2.3. */
  readonly fontFamily: string
  /** `overline` is uppercased in French only — Arabic has no case (§2.3). */
  readonly uppercaseOverline: boolean
}

export const LOCALE_META: Readonly<Record<Locale, LocaleMeta>> = {
  ar: {
    code: 'ar',
    nativeName: 'العربية',
    englishName: 'Arabic',
    direction: 'rtl',
    lineHeight: 'ar',
    fontFamily: 'IBM Plex Sans Arabic',
    uppercaseOverline: false,
  },
  fr: {
    code: 'fr',
    nativeName: 'Français',
    englishName: 'French',
    direction: 'ltr',
    lineHeight: 'fr',
    fontFamily: 'Sora',
    uppercaseOverline: true,
  },
}

/** True when the locale lays out right-to-left. Drives BD-1 … BD-11. */
export function isRTL(locale: Locale): boolean {
  return LOCALE_META[locale].direction === 'rtl'
}

export function directionOf(locale: Locale): Direction {
  return LOCALE_META[locale].direction
}

/** Narrowing guard for values arriving from storage, deep links, or the API. */
export function isSupportedLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

/**
 * Resolve any incoming locale hint (device locale, stored preference, deep link)
 * to a supported locale. Falls back to Arabic — never to the device locale
 * (US-205 criterion 5).
 */
export function resolveLocale(value: unknown): Locale {
  if (isSupportedLocale(value)) return value
  if (typeof value === 'string') {
    const base = value.split(/[-_]/)[0]?.toLowerCase()
    if (isSupportedLocale(base)) return base
  }
  return DEFAULT_LOCALE
}
